import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * MFA Enrollment Screen
 *
 * Shown when a non-test user logs in and has not yet enrolled in MFA.
 * The login route created a session flagged `mfa_enrollment_required: true`
 * — the enforceMfaEnrollment middleware blocks every other API call until
 * enrollment completes. The user lands here, scans a QR with their phone,
 * verifies the first code, and then sees the recovery codes ONCE.
 */

const STEP_INSTALL = 'install';
const STEP_SCAN = 'scan';
const STEP_VERIFY = 'verify';
const STEP_RECOVERY = 'recovery';
const STEP_DONE = 'done';

export default function MfaEnroll() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_INSTALL);
  const [enrollData, setEnrollData] = useState(null);   // { secret, uri, qr_data_url }
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const codeRef = useRef(null);

  const startEnrollment = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/mfa/enroll/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Could not start enrollment.');
        setLoading(false);
        return;
      }
      setEnrollData(data);
      setStep(STEP_SCAN);
    } catch (err) {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = code.trim().replace(/\s+/g, '');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/mfa/enroll/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Invalid code. Make sure your authenticator is showing the current code.');
        setCode('');
        setLoading(false);
        return;
      }
      setRecoveryCodes(data.recovery_codes || []);
      setStep(STEP_RECOVERY);
    } catch (err) {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === STEP_VERIFY && codeRef.current) codeRef.current.focus();
  }, [step]);

  const finishToApp = () => {
    // SSO — if the user was sent here from another app (e.g. Mandate), the
    // login page stashed return_to in sessionStorage; honor it now.
    const returnTo = sessionStorage.getItem('mfa_return_to');
    if (returnTo) sessionStorage.removeItem('mfa_return_to');
    window.location.href = returnTo || '/';
  };

  const downloadRecoveryCodes = () => {
    const blob = new Blob(
      [
        'Precision Compliance Group — MFA Recovery Codes\n',
        '\n',
        'Each code can be used ONCE if you lose access to your authenticator app.\n',
        'Keep these in a safe place (password manager, locked drawer, etc).\n',
        'They are shown only this once.\n',
        '\n',
        ...recoveryCodes.map((c) => `  ${c}\n`),
        '\n',
        `Generated: ${new Date().toISOString()}\n`,
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pcg-mfa-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/4 left-1/3 w-[600px] h-[460px] opacity-30"
          style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)', filter: 'blur(70px)' }}
        />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-blue-500/20 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Set up two-factor authentication</h1>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Your account requires a second factor for login. This takes about two minutes and uses any standard authenticator app.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8">
          {step === STEP_INSTALL && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Step 1 — install an authenticator app</h2>
              <p className="text-sm text-slate-600">
                If you don't already have one on your phone, install any of these (free, takes 30 seconds):
              </p>
              <ul className="text-sm text-slate-700 space-y-2 ml-4 list-disc">
                <li><strong>Microsoft Authenticator</strong> — recommended if you use other Microsoft tools</li>
                <li><strong>Google Authenticator</strong> — simplest, widely used</li>
                <li><strong>1Password</strong> or <strong>Bitwarden</strong> — if you already use one as a password manager</li>
                <li><strong>Authy</strong> — backs up codes across devices</li>
              </ul>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                Important: <strong>do not</strong> use SMS-based 2FA. Per company policy, only authenticator apps and hardware security keys are accepted.
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={startEnrollment}
                disabled={loading}
                className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition-all ${
                  loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-md'
                }`}
              >
                {loading ? 'Loading...' : "I have an authenticator app — show me the QR code"}
              </button>
            </div>
          )}

          {step === STEP_SCAN && enrollData && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Step 2 — scan this QR code</h2>
              <p className="text-sm text-slate-600">
                Open your authenticator app, tap "Add account" or the "+" icon, then scan the QR below.
              </p>

              <div className="flex justify-center">
                <div className="p-4 bg-white border-2 border-slate-200 rounded-xl shadow-inner">
                  <img src={enrollData.qr_data_url} alt="MFA QR code" width={256} height={256} />
                </div>
              </div>

              <details className="text-sm text-slate-600">
                <summary className="cursor-pointer hover:text-slate-900 font-medium">Can't scan the QR? Enter this code manually instead.</summary>
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-md font-mono text-xs break-all select-all">
                  {enrollData.secret}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Account: <span className="font-mono">{enrollData.account}</span> · Issuer:{' '}
                  <span className="font-mono">{enrollData.issuer}</span>
                </p>
              </details>

              <button
                type="button"
                onClick={() => setStep(STEP_VERIFY)}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all"
              >
                Next — enter the 6-digit code
              </button>
            </div>
          )}

          {step === STEP_VERIFY && (
            <form onSubmit={verifyCode} className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Step 3 — confirm it's working</h2>
              <p className="text-sm text-slate-600">
                Type the current 6-digit code shown by your authenticator app. The code rotates every 30 seconds — if it changes mid-typing, just enter the new one.
              </p>

              <div>
                <label htmlFor="enroll-code" className="block text-sm font-medium text-slate-700 mb-1">
                  6-digit code
                </label>
                <input
                  ref={codeRef}
                  id="enroll-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="w-full px-3 py-2.5 text-lg tracking-widest text-center font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(STEP_SCAN)}
                  className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !code.trim()}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-white transition-all ${
                    loading || !code.trim()
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 shadow-md'
                  }`}
                >
                  {loading ? 'Verifying...' : 'Confirm and enable'}
                </button>
              </div>
            </form>
          )}

          {step === STEP_RECOVERY && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Step 4 — save your recovery codes</h2>
              <div className="rounded-lg bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900">
                <strong>Important:</strong> these are the only way back into your account if you lose your phone. Each code works once.
                <br />
                <strong>You will not see them again.</strong> Save them now in a password manager, print them, or screenshot.
              </div>

              <div className="grid grid-cols-2 gap-2 p-4 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm">
                {recoveryCodes.map((c) => (
                  <div key={c} className="px-2 py-1 select-all">
                    {c}
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={downloadRecoveryCodes}
                  className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-all"
                >
                  Download as text file
                </button>
                <button
                  type="button"
                  onClick={() => setStep(STEP_DONE)}
                  className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all"
                >
                  I've saved them
                </button>
              </div>
            </div>
          )}

          {step === STEP_DONE && (
            <div className="space-y-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Two-factor authentication is on</h2>
              <p className="text-sm text-slate-600">
                Next time you sign in, you'll be asked for the current 6-digit code from your authenticator app.
              </p>
              <button
                type="button"
                onClick={finishToApp}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all"
              >
                Continue to the app
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
